/**
 * @file FormField.tsx
 * @description Label + input + error message. Pure presentational; parent
 *   manages value/onChange/error state. Uses CSS logical properties so the
 *   layout flips automatically under RTL.
 * @module Components
 * @security No HTML in label or error — both are i18n strings; React
 *   escapes them automatically.
 */

import type { InputHTMLAttributes, JSX } from 'react';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}

export function FormField({ id, label, error, className, ...rest }: FormFieldProps): JSX.Element {
  const describedBy = error !== undefined ? `${id}-error` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        className={[
          'px-3 py-2 text-sm',
          error !== undefined ? 'border-red-500' : '',
          className ?? '',
        ].join(' ')}
        {...rest}
      />
      {error !== undefined ? (
        <p id={describedBy} className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
